import React, { useEffect } from 'react';
import { IoCloseSharp } from "react-icons/io5";
import { Download, ExternalLink } from 'lucide-react';
import { createPortal } from 'react-dom';

interface ImagePreviewModalProps {
  src: string;
  isOpen: boolean;
  onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ src, isOpen, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    // For base64 or blob URLs
    const link = document.createElement('a');
    link.href = src;
    link.download = `worklap-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenNewTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (src.startsWith('data:')) {
      // For base64 images, we need to convert to a blob to open in a new tab reliably
      try {
        const parts = src.split(';base64,');
        const contentType = parts[0].split(':')[1];
        const raw = window.atob(parts[1]);
        const rawLength = raw.length;
        const uInt8Array = new Uint8Array(rawLength);
        
        for (let i = 0; i < rawLength; ++i) {
          uInt8Array[i] = raw.charCodeAt(i);
        }
        
        const blob = new Blob([uInt8Array], { type: contentType });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      } catch (error) {
        console.error('Failed to open base64 image in new tab:', error);
        window.open(src, '_blank');
      }
    } else {
      window.open(src, '_blank');
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Header with gradient for visibility */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/60 to-transparent z-[100] pointer-events-none" />
      
      <div className="absolute top-6 right-6 flex items-center gap-3 z-[101] pointer-events-auto">
        <button
          onClick={handleDownload}
          className="group p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all border border-white/10 shadow-xl backdrop-blur-md flex items-center gap-2 text-sm font-medium pr-5"
          title="Download Image"
        >
          <div className="bg-blue-500 p-1.5 rounded-full group-hover:scale-110 transition-transform">
            <Download size={16} />
          </div>
          <span>Download</span>
        </button>

        <button
          onClick={handleOpenNewTab}
          className="p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all border border-white/10 shadow-xl backdrop-blur-md hover:scale-105"
          title="Open in New Tab"
        >
          <ExternalLink size={20} />
        </button>

        <button
          onClick={onClose}
          className="p-3 rounded-full bg-red-500/20 hover:bg-red-500/40 text-red-500 transition-all border border-red-500/20 shadow-xl backdrop-blur-md hover:rotate-90 transition-all duration-300"
          title="Close"
        >
          <IoCloseSharp size={24} />
        </button>
      </div>

      <div 
        className="relative max-w-[95vw] max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex items-center justify-center animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <img 
          src={src} 
          alt="Preview" 
          className="max-w-full max-h-[90vh] object-contain select-none shadow-2xl border border-white/5"
        />
      </div>


    </div>,
    document.body
  );
};

export default ImagePreviewModal;
